/**
 * PromptVersionList — this team's prompt history, newest first.
 *
 * The shipped default is always the LAST row: it is what v1 was edited from,
 * so without it in the list the very first revision has nothing to diff
 * against. It is display-only — there is no stored row to activate, and the
 * Edit tab's "Reset to default" is the one way back to it.
 *
 * History is append-only, so "roll back" activates an old revision rather
 * than rewriting anything. Selecting a row diffs it against the revision
 * BEFORE it — the question a history answers is "what did this edit change",
 * which a diff against live can't tell you (it collapses every intervening
 * revision, and reads as empty for the live row itself).
 */

import { RotateCcw } from 'lucide-react'

import type { AgentPromptVersion } from '@/hooks/useAgentPrompts'
import { PromptDiffView } from './PromptDiffView'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { formatRelativeTime } from '@/lib/utils'
import { cn } from '@/lib/utils'

/**
 * Stand-in version number for the shipped default. Real revisions start at
 * 1, so 0 reads as "before the first edit" and needs no extra selection
 * state.
 */
const DEFAULT_VERSION = 0

interface HistoryEntry {
  key: string
  version: number
  label: string
  byline: string
  body: string
  isActive: boolean
  /** Only stored revisions can be activated; the default has no row. */
  canActivate: boolean
}

function toEntries(
  versions: AgentPromptVersion[],
  defaultBody: string,
  defaultIsLive: boolean
): HistoryEntry[] {
  const saved = versions.map(
    (v): HistoryEntry => ({
      key: v.id,
      version: v.version,
      label: `v${String(v.version)}`,
      byline: `${v.created_by} · ${formatRelativeTime(v.created_at)}`,
      body: v.body,
      isActive: v.is_active,
      canActivate: !v.is_active,
    })
  )
  return [
    ...saved,
    {
      key: 'shipped-default',
      version: DEFAULT_VERSION,
      label: 'Default',
      byline: 'shipped with TrainLoop',
      body: defaultBody,
      isActive: defaultIsLive,
      canActivate: false,
    },
  ]
}

interface PromptVersionListProps {
  versions: AgentPromptVersion[]
  defaultBody: string
  /** True when no team override exists, so the default is what runs. */
  defaultIsLive: boolean
  selectedVersion: number | null
  onSelect: (version: number | null) => void
  onActivate: (version: number) => void
  isActivating: boolean
  disabled: boolean
}

export function PromptVersionList({
  versions,
  defaultBody,
  defaultIsLive,
  selectedVersion,
  onSelect,
  onActivate,
  isActivating,
  disabled,
}: PromptVersionListProps): React.JSX.Element {
  const entries = toEntries(versions, defaultBody, defaultIsLive)
  // Newest-first, so a row's predecessor is the NEXT one down.
  const selectedIndex = entries.findIndex((e) => e.version === selectedVersion)
  const selected = selectedIndex === -1 ? null : entries[selectedIndex]
  const previous = selectedIndex === -1 ? undefined : entries[selectedIndex + 1]

  return (
    <div className="space-y-3">
      <ul className="divide-y rounded-md border">
        {entries.map((entry) => {
          const isSelected = entry.version === selectedVersion
          return (
            <li key={entry.key}>
              <div className="flex items-center gap-3 px-3 py-2">
                <button
                  type="button"
                  onClick={() => { onSelect(isSelected ? null : entry.version) }}
                  className={cn(
                    'flex min-w-0 flex-1 items-center gap-2 text-left text-sm',
                    isSelected && 'font-medium'
                  )}
                >
                  <span className="tabular-nums">{entry.label}</span>
                  {entry.isActive && <Badge variant="secondary">Live</Badge>}
                  <span className="truncate text-muted-foreground">{entry.byline}</span>
                </button>
                {entry.canActivate && (
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={disabled || isActivating}
                    onClick={() => { onActivate(entry.version) }}
                  >
                    <RotateCcw className="mr-1 h-3 w-3" />
                    Activate
                  </Button>
                )}
              </div>
            </li>
          )
        })}
      </ul>
      {selected &&
        (previous === undefined ? (
          // Only the shipped default can land here — nothing precedes it.
          <p className="text-sm text-muted-foreground">
            The shipped default. Every revision starts here, so there is
            nothing before it to compare against.
          </p>
        ) : (
          <PromptDiffView
            before={previous.body}
            after={selected.body}
            beforeLabel={previous.label}
            afterLabel={selected.label}
          />
        ))}
    </div>
  )
}
